import { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  Sparkles, X, BookOpen, Brain, FileSpreadsheet, FileText, MapPin, Globe,
  PanelLeftOpen,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { useTeamStore } from "@/lib/hooks/useTeam"
import { useUser } from "@/lib/hooks/useUser"
import { usePermissions } from "@/lib/hooks/usePermissions"
import { atLeast, isServiceLive, type CmsAccess, type CmsSection } from "./shared/permissions"
import { EditorAccessNotice } from "./shared/components/EditorAccessNotice"
import { nodeTitle, articleState } from "./articles/types"
import { useLocalCMS } from "./articles/useLocalCMS"
import { useAssistant } from "@/components/assistant/context"
import { useSmartCmsAssistant } from "@/lib/assistant/modules/smartCms"
import type { WorkflowToolContext } from "@/lib/assistant/modules/smartCms/workflow/tools"
import { LanguagesPanel } from "./shared/components/languages/LanguagesPanel"
import { LanguageSwitcher } from "./shared/components/languages/LanguageSwitcher"
import { useTeamLanguages } from "./shared/useTeamLanguages"
import { percent } from "./shared/languages"
import { SVC_KIND_OF_BUCKET, type SectionKey } from "./shared/localeSyncAdapters"
import { buildLocaleCopy, type SyncPlan } from "./shared/localeSync"
import { SyncIndicator } from "./shared/components/SyncIndicator"
import { SearchIndexStatus } from "./shared/components/SearchIndexStatus"
import { PendingChangesButton, PendingIdsContext, type DueItem, type PendingItem } from "./shared/components/PendingChanges"
import { FreshnessChip } from "./shared/components/FreshnessChip"
import { MediaSourcesContext } from "./shared/components/MediaLibrary"
import { setUploadTeam } from "./shared/uploadImage"
import { ago, freshnessOf, useRecordMeta, useTeamMembers } from "./shared/useRecordMeta"
import type { RecordStatus } from "./shared/components/RecordStatusBar"
import { describeEdit } from "./shared/useRecordDraft"
import { BUCKET as SVC_BUCKET } from "./service-map/types"
import { SITECONFIG_BUCKET } from "./site-config/types"
import { IndexBadge } from "./shared/components/IndexBadge"
import { useSearchIndex } from "./shared/search/useSearchIndex"
import { ArticleTreeBrowser } from "./articles/ArticleTreeBrowser"
import { ArticleNodeEditor } from "./articles/ArticleNodeEditor"
import * as cmsStore from "./articles/store"
import { useServiceCMS } from "./service-map/useServiceCMS"
import { ServiceBrowser } from "./service-map/ServiceBrowser"
import { ServiceEditor } from "./service-map/ServiceEditor"
import { parseSheet, type ParsedSheet } from "./service-map/import"
import { displayName, serviceStatusFor } from "./service-map/types"
import { useSiteConfigCMS } from "./site-config/useSiteConfigCMS"
import { SiteConfigBrowser } from "./site-config/SiteConfigBrowser"
import { SiteConfigEditor } from "./site-config/SiteConfigEditor"

type Backend = "cms" | "services" | "siteconfig"

const BACKEND_SECTION: Record<Backend, CmsSection> = { cms: "articles", services: "serviceMap", siteconfig: "siteConfig" }

/**
 * The four backends, in one place.
 *
 * Label, icon and browser heading were previously spelled out separately at
 * each use site, so a rename meant finding every copy — this is the single
 * source of truth they all read from.
 */
const BACKENDS = [
  { value: "cms", label: "Local Articles", icon: FileText, browserTitle: "Content Tree" },
  { value: "services", label: "Service Map", icon: MapPin, browserTitle: "Service Map" },
  { value: "siteconfig", label: "Site Config", icon: Globe, browserTitle: "Site Config" },
] as const

// localStorage keys keep the old "kb-agent-" prefix: they are data addresses, renaming them loses saved state
const DEFAULT_TEAM = "default"


// ── Main Component ────────────────────────────────────────────────────────────

export function SmartCMSPage() {
  const { selectedTeam } = useTeamStore()
  const { data: currentUser } = useUser()
  const teamId = selectedTeam?.id ?? DEFAULT_TEAM
  const { cms: cmsPerms } = usePermissions()
  const assistant = useAssistant()
  const accessFor = (b: Backend): CmsAccess => cmsPerms[BACKEND_SECTION[b]]

  // Which CMS the page is editing.
  // Which CMS the page is editing. Persisted so a reload lands where you were.
  const [backend, setBackend] = useState<Backend>(
    () => (localStorage.getItem("kb-agent-backend") as Backend) ?? "cms"
  )
  useEffect(() => {
    localStorage.setItem("kb-agent-backend", backend)
  }, [backend])
  const isCMS = backend === "cms"
  const isSvc = backend === "services"
  const isSiteConfig = backend === "siteconfig"
  const allowedBackends = BACKENDS.filter(b => atLeast(accessFor(b.value), "view"))
  const backendAccess = accessFor(backend)
  useEffect(() => {
    if (!atLeast(accessFor(backend), "view") && allowedBackends.length) setBackend(allowedBackends[0].value)
  }, [backend, cmsPerms]) // eslint-disable-line react-hooks/exhaustive-deps


  // Attribution for the change log. Agent-made changes are credited to the
  // signed-in user who prompted them, with the model recorded alongside.
  const cmsAuthor = useMemo(() => ({
    name: [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(" ").trim() || undefined,
    email: currentUser?.email,
    id: currentUser?.id,
    model: assistant.model,
  }), [currentUser?.first_name, currentUser?.last_name, currentUser?.email, currentUser?.id, assistant.model])

  const [localesOpen, setLocalesOpen] = useState(false)

  // Sections the role can't see are never loaded: an empty team id keeps the hook offline
  const cms = useLocalCMS(cmsAuthor, atLeast(cmsPerms.articles, "view") ? teamId : "", cmsPerms.articles)
  const cmsStats = cmsStore.stats(cms.store)
  const svc = useServiceCMS(cmsAuthor, atLeast(cmsPerms.serviceMap, "view") ? teamId : "", cmsPerms.serviceMap)
  const siteconfig = useSiteConfigCMS(cmsAuthor, atLeast(cmsPerms.siteConfig, "view") ? teamId : "", cmsPerms.siteConfig)

  // Editors (not managers) can't change live content; the editor shows a notice for it
  const selectedArticle = cms.selectedId ? cms.store.articles[cms.selectedId] : undefined
  const articleIsLive = !!selectedArticle && articleState(selectedArticle, cms.locale) === "published"
  const serviceIsLive = svc.selected?.kind === "service" && isServiceLive(svc.store.services[svc.selected.id])

  // One working language and one language list across all three sections
  const langs = useTeamLanguages({ cms, svc, siteconfig, perms: cmsPerms })
  const searchIndex = useSearchIndex({
    teamId: teamId === DEFAULT_TEAM ? "" : teamId,
    // Only published content is indexed, never unpublished changes
    cms: { store: cms.pending.liveStore, loaded: cms.loaded },
    svc: { store: svc.pending.liveStore, loaded: svc.loaded },
    siteconfig: { store: siteconfig.pending.liveStore, loaded: siteconfig.loaded },
    perms: cmsPerms,
  })

  // Owners, review schedules and review requests, per section
  const metaTeam = teamId === DEFAULT_TEAM ? "" : teamId
  // New uploads go in the team's folder, which the media library lists
  useEffect(() => { setUploadTeam(metaTeam) }, [metaTeam])
  // Every record the media library looks for images in
  const mediaSources = useCallback(() => [
    { label: "Article", records: cms.store.articles },
    { label: "Service", records: svc.store.services },
    { label: "Provider", records: svc.store.providers },
    { label: "Service category", records: svc.store.serviceCategories },
    { label: "Site", records: siteconfig.store.sites },
    { label: "Page block", records: siteconfig.store.pageContents },
  ], [cms.store, svc.store, siteconfig.store])
  const cmsMeta = useRecordMeta(metaTeam, "articles", cmsAuthor)
  const svcMeta = useRecordMeta(metaTeam, "services", cmsAuthor)
  const siteMeta = useRecordMeta(metaTeam, "site", cmsAuthor)
  const sectionMeta = isSiteConfig ? siteMeta : isSvc ? svcMeta : cmsMeta
  const people = useTeamMembers(metaTeam)
  const myName = cmsAuthor.name || cmsAuthor.email || "Someone"

  // Unpublished changes in the open section
  const sectionPending = isSiteConfig ? siteconfig.pending : isSvc ? svc.pending : cms.pending
  const pendingIds = useMemo(() => new Set(sectionPending.keys.map(k => k.slice(k.indexOf(":") + 1))), [sectionPending.keys])
  const workingStore = (isSiteConfig ? siteconfig.store : isSvc ? svc.store : cms.store) as any
  const kindOfBucket = (bucket: string, map: Record<string, string>) => Object.keys(map).find(k => map[k] === bucket) ?? bucket
  // A record's kind and title, from its "bucket:id" key
  const describeKey = (key: string) => {
    const i = key.indexOf(":")
    const bucket = key.slice(0, i)
    const id = key.slice(i + 1)
    const entity = workingStore[bucket]?.[id]
    let kind = bucket
    let title = id
    if (entity && isCMS) { kind = entity.kind; title = nodeTitle(entity, cms.locale, cms.store.defaultLocale) }
    if (entity && isSvc) { kind = kindOfBucket(bucket, SVC_BUCKET); title = displayName(entity, svc.locale, svc.store.defaultLocale) }
    if (entity && isSiteConfig) { kind = kindOfBucket(bucket, SITECONFIG_BUCKET); title = siteconfig.nameOf(kind as any, id) }
    return { bucket, id, entity, kind, title }
  }
  const pendingItems: PendingItem[] = sectionPending.keys.map(key => {
    const { bucket, id, entity, kind, title } = describeKey(key)
    return {
      key, title,
      kind: kind.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase(),
      summary: describeEdit(sectionPending.liveEntity(bucket, id), entity),
      reviewer: sectionMeta.meta[key]?.review ? (sectionMeta.meta[key].review!.assignee?.name ?? "any manager") : undefined,
      assignedToMe: !!currentUser?.id && sectionMeta.meta[key]?.review?.assignee?.id === currentUser.id,
    }
  })
  const openPending = (key: string) => {
    const i = key.indexOf(":")
    const bucket = key.slice(0, i)
    const id = key.slice(i + 1)
    if (isCMS) cms.setSelectedId(id)
    if (isSvc) svc.setSelected({ kind: kindOfBucket(bucket, SVC_BUCKET) as any, id })
    if (isSiteConfig) siteconfig.setSelected({ kind: kindOfBucket(bucket, SITECONFIG_BUCKET) as any, id })
    setEditorOpen(true)
  }
  // Where an open record stands, for the strip under its editor
  const recordStatusFor = (bucket: string | undefined, id: string | null | undefined, publishing?: {
    state: "draft" | "published"
    label?: string
    scope?: string
    publish: () => void
    unpublish: () => void
  }): RecordStatus | undefined => {
    if (!bucket || !id) return undefined
    const key = sectionPending.keyOf(bucket, id)
    const pending = sectionPending.keys.includes(key)
      ? { summary: describeEdit(sectionPending.liveEntity(bucket, id), workingStore[bucket]?.[id]) }
      : null
    // Publishing or unpublishing is one step: the change, then putting it live
    const thenLive = (change: () => void) => () => { change(); sectionPending.publish([key]); sectionMeta.clearReview(key) }
    return {
      state: publishing?.state ?? "live",
      label: publishing?.label,
      scope: publishing?.scope,
      pending,
      canPublish: sectionPending.canPublish,
      onPublishChanges: () => { sectionPending.publish([key]); sectionMeta.clearReview(key) },
      onDiscardChanges: () => { sectionPending.discard([key]); sectionMeta.clearReview(key) },
      review: sectionMeta.meta[key]?.review ?? null,
      people,
      onRequestReview: assignee => sectionMeta.update(key, { review: { assignee, requestedBy: myName, requestedAt: Date.now() } }),
      onCancelReview: () => sectionMeta.clearReview(key),
      onPublish: publishing && thenLive(publishing.publish),
      onUnpublish: publishing && thenLive(publishing.unpublish),
    }
  }
  const articleBucketOf = (id: string | null) =>
    !id ? undefined : cms.store.articles[id] ? "articles" : cms.store.sections[id] ? "sections" : cms.store.categories[id] ? "categories" : undefined

  // Live content past, or near, its review date; drafts aren't checked
  const dueItems: DueItem[] = useMemo(() => {
    const out: DueItem[] = []
    const add = (bucket: string, entity: any, title: string, kind: string) => {
      const f = freshnessOf(entity, sectionMeta.meta[sectionPending.keyOf(bucket, entity.id)])
      if (f && f.state !== "ok") {
        out.push({ key: sectionPending.keyOf(bucket, entity.id), title, kind, overdue: f.state === "overdue", detail: `Last checked ${ago(f.checkedAt)}` })
      }
    }
    if (isCMS) {
      for (const a of Object.values(cms.store.articles)) {
        if (Object.values(a.translations ?? {}).some((t: any) => (t?.state ?? a.state) === "published")) {
          add("articles", a, nodeTitle(a, cms.locale, cms.store.defaultLocale), "article")
        }
      }
    }
    if (isSvc) {
      for (const sv of Object.values(svc.store.services)) if (isServiceLive(sv)) add("services", sv, displayName(sv, svc.locale, svc.store.defaultLocale), "service")
      for (const pr of Object.values(svc.store.providers)) if (pr.status !== "Not Active") add("providers", pr, displayName(pr, svc.locale, svc.store.defaultLocale), "provider")
    }
    return out.sort((a, b) => Number(b.overdue) - Number(a.overdue))
  }, [isCMS, isSvc, cms.store, svc.store, cms.locale, svc.locale, sectionMeta.meta, sectionPending.keyOf])

  // What the list filters need about each record: unpublished changes, review date, owner
  const pendingKeySet = useMemo(() => new Set(sectionPending.keys), [sectionPending.keys])
  const recordInfoFor = (bucket: string, id: string) => {
    const key = sectionPending.keyOf(bucket, id)
    const m = sectionMeta.meta[key]
    return {
      pending: pendingKeySet.has(key),
      review: freshnessOf(workingStore[bucket]?.[id], m)?.state ?? null,
      ownerId: m?.owner?.id,
    }
  }

  // Editor header chip: when the open record was last checked
  const freshnessChip = (bucket: string | undefined, id: string | null | undefined) => {
    const entity = bucket && id ? workingStore[bucket]?.[id] : undefined
    if (!entity) return null
    const key = sectionPending.keyOf(bucket!, id!)
    return (
      <FreshnessChip
        freshness={freshnessOf(entity, sectionMeta.meta[key])}
        meta={sectionMeta.meta[key]}
        people={people}
        canEdit={atLeast(backendAccess, "edit") && sectionMeta.available}
        onUpdate={patch => sectionMeta.update(key, patch.verifiedAt ? { ...patch, verifiedBy: myName } : patch)}
      />
    )
  }

  const workingLanguage = langs.infoOf(langs.working)
  const sectionOf = BACKEND_SECTION[backend] as SectionKey

  // Articles publish per language
  const articleStatus = (() => {
    const id = cms.selectedId
    const bucket = articleBucketOf(id)
    const article = id ? cms.store.articles[id] : undefined
    if (!id || bucket !== "articles" || !article?.translations[cms.locale]) return recordStatusFor(bucket, id)
    const setState = (state: "published" | "draft") => () => cms.commitEdit(
      cmsStore.setArticleState(cms.getStore(), id, state, { locale: cms.locale }),
      id, `${state === "published" ? "Published" : "Unpublished"} ${workingLanguage.name}`,
    )
    return recordStatusFor(bucket, id, {
      state: articleState(article, cms.locale) === "published" ? "published" : "draft",
      scope: workingLanguage.name,
      publish: setState("published"),
      unpublish: setState("draft"),
    })
  })()

  // Services publish per language too; other Service Map records have no publish state
  const serviceStatus = (() => {
    const sel = svc.selected
    if (!sel) return undefined
    const service = sel.kind === "service" ? svc.store.services[sel.id] : undefined
    if (!service) return recordStatusFor(SVC_BUCKET[sel.kind], sel.id)
    const status = serviceStatusFor(service, svc.locale)
    const setStatus = (next: string) => () => svc.setTranslation("service", sel.id, svc.locale, { status: next })
    return recordStatusFor(SVC_BUCKET[sel.kind], sel.id, {
      state: status === "published" ? "published" : "draft",
      label: status,
      scope: workingLanguage.name,
      publish: setStatus("published"),
      unpublish: setStatus("draft"),
    })
  })()

  // Applies an approved copy through the section's own bulk write, so history and sync still happen
  const handleCopyLocale = useCallback((
    section: SectionKey,
    plan: SyncPlan,
    approvedKeys: string[],
  ) => {
    const sources = langs.sections.find(s => s.section === section)?.sources ?? []
    const { writes } = buildLocaleCopy(sources, plan, approvedKeys)
    if (!writes.size) return
    const list = [...writes.values()]
    const summary = `Copied ${plan.from} to ${plan.to} (unreviewed)`

    if (section === "articles") {
      cms.applyTranslations(
        list.map(w => ({ id: w.id, translation: w.translation })),
        plan.to, summary,
      )
    } else if (section === "serviceMap") {
      svc.applyTranslations(
        list.flatMap(w => {
          // Skip buckets with no EntityKind (not translatable)
          const kind = SVC_KIND_OF_BUCKET.get(w.bucket)
          if (!kind) return []
          return [{
          kind,
          id: w.id,
          patch: {
            name: w.translation.name ?? "",
            description: w.translation.description,
            address: w.translation.address,
            definition: w.translation.definition,
            reviewed: false,
            status: w.translation.status,
          },
          }]
        }),
        plan.to, summary,
      )
    } else {
      siteconfig.applyTranslations(
        list.map(w => ({ kind: "pageContent" as const, id: w.id, translation: w.translation })),
        plan.to, summary,
      )
    }
  }, [langs.sections, cms, svc, siteconfig])

  /*
   * A Site REFERENCES geography and article categories rather than owning
   * them (see site-config/types.ts FIDELITY NOTE 1), so the agent needs the
   * referenceable ids from the other two backends to wire up a scope. These
   * are read-only projections — site config never mutates them.
   */
  const siteconfigCountries = useMemo(() => Object.values(svc.store.countries).map(c => ({
    id: c.id,
    name: displayName(c, svc.locale, svc.store.defaultLocale),
  })), [svc.store.countries, svc.locale, svc.store.defaultLocale])

  const siteconfigRegions = useMemo(() => Object.values(svc.store.regions).map(r => ({
    id: r.id, name: r.name,
  })), [svc.store.regions])

  const siteconfigCities = useMemo(() => Object.values(svc.store.cities).map(c => ({
    id: c.id, name: c.name,
  })), [svc.store.cities])

  const siteconfigArticleCategories = useMemo(() => Object.values(cms.store.categories).map(c => ({
    id: c.id,
    name: nodeTitle(c, cms.locale, cms.store.defaultLocale),
  })), [cms.store.categories, cms.locale, cms.store.defaultLocale])

  // The spreadsheet the user has uploaded for bulk import, if any. Kept in a
  // ref alongside React state for the same reason the store is: an agent turn
  // makes several tool calls without a re-render in between, and a captured
  // snapshot from render time would go stale after the first one.
  const [uploadedSheet, setUploadedSheet] = useState<ParsedSheet | null>(null)
  const uploadedSheetRef = useRef<ParsedSheet | null>(null)
  const [sheetError, setSheetError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleSheetFile = useCallback(async (file: File) => {
    setSheetError(null)
    try {
      const buf = await file.arrayBuffer()
      const parsed = parseSheet(buf)
      if (!parsed.headers.length || !parsed.rows.length) {
        setSheetError(`"${file.name}" has no readable rows. Check it's a CSV or Excel file with a header row.`)
        return
      }
      uploadedSheetRef.current = parsed
      setUploadedSheet(parsed)
    } catch (e: any) {
      setSheetError(`Could not read "${file.name}": ${e?.message || String(e)}`)
    }
  }, [])

  const clearUploadedSheet = useCallback(() => {
    uploadedSheetRef.current = null
    setUploadedSheet(null)
    setSheetError(null)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }, [])

  // Both side panels are user-draggable. The tree holds long titles and deep
  // nesting, and articles are easier to read wide, so fixed widths clipped one
  // or the other. Widths persist so the layout survives a reload.
  const SIDEBAR_DEFAULT = 288

  const readStoredWidth = (key: string, fallback: number, min: number) => {
    const saved = Number(localStorage.getItem(key))
    return Number.isFinite(saved) && saved >= min ? saved : fallback
  }

  const [sidebarWidth, setSidebarWidth] = useState(() =>
    readStoredWidth("kb-agent-sidebar-width", SIDEBAR_DEFAULT, 200))
  // Whether the sidebar handle is being dragged. A ref (not state) so the
  // move listener doesn't need re-binding on every drag.
  const draggingRef = useRef<"sidebar" | null>(null)

  useEffect(() => {
    localStorage.setItem("kb-agent-sidebar-width", String(sidebarWidth))
  }, [sidebarWidth])

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!draggingRef.current) return
      // Left panel grows rightward: width tracks the cursor's x directly.
      // The editor flexes into whatever is left, so it needs no handle.
      setSidebarWidth(Math.max(200, Math.min(e.clientX, 720)))
    }
    const onUp = () => {
      if (!draggingRef.current) return
      draggingRef.current = null
      document.body.style.cursor = ""
      document.body.style.userSelect = ""
    }
    window.addEventListener("mousemove", onMove)
    window.addEventListener("mouseup", onUp)
    return () => {
      window.removeEventListener("mousemove", onMove)
      window.removeEventListener("mouseup", onUp)
    }
  }, [])

  const startResize = useCallback((which: "sidebar") => {
    draggingRef.current = which
    // Set on body so the cursor and no-select hold while dragging over children.
    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"
  }, [])

  // Layout
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [editorOpen, setEditorOpen] = useState(false)

  // Deep link: /smart-cms?open=<cms path>. Consumed once, then cleared.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    const open = searchParams.get("open")
    if (!open) return

    // Same shape parsePath produces in the navigator: area, then kind:id.
    const [area, ...rest] = open.split("/").filter(Boolean)
    const last = rest[rest.length - 1]
    const [kind, id] = last ? [last.slice(0, last.indexOf(":")), last.slice(last.indexOf(":") + 1)] : []

    if (area === "articles") setBackend("cms")
    else if (area === "services") setBackend("services")
    else if (area === "site") setBackend("siteconfig")

    if (id) {
      if (area === "articles") cms.setSelectedId(id)
      // The service map keys its selection by entity kind as well as id.
      else if (area === "services") svc.setSelected({ kind: kind as any, id })
      setEditorOpen(true)
    }

    searchParams.delete("open")
    setSearchParams(searchParams, { replace: true })
  }, [searchParams, setSearchParams, cms.setSelectedId, svc.setSelected])

  // Reviews, owners, publishing, filters and media for the assistant; read through a ref so a long run sees current state
  const sectionBuckets = isCMS ? ["articles", "sections", "categories"] : Object.values(isSvc ? SVC_BUCKET : SITECONFIG_BUCKET) as string[]
  const workflowState = useRef<any>(null)
  workflowState.current = {
    sectionBuckets, sectionPending, getStore: isSiteConfig ? siteconfig.getStore : isSvc ? svc.getStore : cms.getStore, sectionMeta, pendingItems, dueItems, people, describeKey, openPending, mediaSources,
    me: { id: currentUser?.id, name: myName },
    versions: (id: string) => isCMS
      ? cms.versionsOf(id).map(v => ({ entryId: v.entryId, at: v.at, author: v.author, summary: v.summary, snapshot: v.node as any }))
      : (isSvc ? svc.versionsOf(id) : siteconfig.versionsOf(id)).map(v => ({ entryId: v.entryId, at: v.at, author: v.author, summary: v.summary, snapshot: v.entity })),
  }
  const workflow = useMemo<WorkflowToolContext>(() => {
    const w = () => workflowState.current
    return {
      filterSection: isCMS ? "articles" : isSvc ? "services" : null,
      keyFor: id => {
        const bucket = w().sectionBuckets.find((b: string) => (w().getStore() as any)[b]?.[id])
        return bucket ? w().sectionPending.keyOf(bucket, id) : null
      },
      titleOf: key => w().describeKey(key).title,
      working: key => { const { bucket, id } = w().describeKey(key); return (w().getStore() as any)[bucket]?.[id] },
      live: key => { const { bucket, id } = w().describeKey(key); return w().sectionPending.liveEntity(bucket, id) },
      pending: () => w().pendingItems,
      isPending: key => w().sectionPending.allKeys().includes(key),
      due: () => w().dueItems,
      meta: () => w().sectionMeta.current(),
      metaAvailable: () => w().sectionMeta.available,
      people: () => w().people,
      me: () => w().me,
      canPublish: () => w().sectionPending.canPublish,
      updateMeta: (key, patch) => w().sectionMeta.update(key, patch),
      clearReview: key => w().sectionMeta.clearReview(key),
      publish: key => w().sectionPending.publish([key]),
      discard: key => w().sectionPending.discard([key]),
      versions: id => w().versions(id),
      open: key => w().openPending(key),
      mediaSources: () => w().mediaSources(),
    }
  }, [isCMS, isSvc])

  // The open section's assistant module
  useSmartCmsAssistant({
    backend, access: backendAccess, cms, svc, siteconfig, setEditorOpen,
    getSheet: () => uploadedSheetRef.current,
    siteconfigCountries, siteconfigRegions, siteconfigCities, siteconfigArticleCategories,
    workflow,
  })

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <MediaSourcesContext.Provider value={mediaSources}>
    <div className="flex flex-col h-[calc(100vh-64px)] min-h-0">

      {/* Top bar; a container, so labels shrink to icons when the CMS is narrow */}
      <div className="@container flex items-center gap-3 px-4 py-2 border-b shrink-0">
        <div className="flex items-center gap-2 shrink-0">
          <Brain className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          <span className="text-sm font-semibold tracking-tight">Smart CMS</span>
        </div>

        {/*
          Backend switcher. A segmented control rather than loose buttons:
          these are mutually exclusive views of one workspace, and the shared
          track makes that reading immediate. Labels are nowrap — two of them
          are two words and wrapped to a second line otherwise, which pushed
          the whole bar taller.
        */}
        <div
          role="tablist"
          aria-label="Backend"
          className="flex items-center gap-0.5 rounded-lg bg-muted p-0.5"
        >
          {allowedBackends.map(({ value, label, icon: Icon }) => {
            const active = backend === value
            return (
              <button
                key={value}
                role="tab"
                aria-selected={active}
                onClick={() => setBackend(value)}
                title={label}
                className={cn(
                  "flex items-center justify-center rounded-md px-2 py-1.5 transition-all",
                  active
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {/* Icon only; the name is on hover and for screen readers */}
                <Icon className="h-4 w-4 shrink-0" />
                <span className="sr-only">{label}</span>
              </button>
            )
          })}
        </div>

        <div className="flex-1" />

        {/*
          Which backend the CMS is writing to. Local-only is a real safety
          property, so it is shown rather than buried in a settings page.
          Reads whichever store owns the active backend.
        */}
        <SyncIndicator
          status={isSiteConfig ? siteconfig.syncStatus : isSvc ? svc.syncStatus : cms.syncStatus}
          onFlush={isSiteConfig ? siteconfig.flushSync : isSvc ? svc.flushSync : cms.flushSync}
        />

        {/* Spreadsheet for the assistant to import into the Service Map */}
        {isSvc && atLeast(backendAccess, "edit") && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={e => {
                const file = e.target.files?.[0]
                if (file) void handleSheetFile(file)
                e.target.value = ""
              }}
            />
            {uploadedSheet ? (
              <span className="flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs">
                <FileSpreadsheet className="h-3.5 w-3.5 text-emerald-600" />
                {uploadedSheet.activeSheet || "Sheet"} · {uploadedSheet.rows.length} rows
                <button type="button" title="Remove spreadsheet" onClick={clearUploadedSheet} className="ml-1 text-muted-foreground hover:text-foreground">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ) : (
              <Button
                variant="ghost" size="sm" className="h-7 gap-1.5 text-xs"
                title="Upload a CSV or Excel file, then ask the assistant to import it"
                onClick={() => fileInputRef.current?.click()}
              >
                <FileSpreadsheet className="h-3.5 w-3.5" />
                <span className="hidden @2xl:inline">Import spreadsheet</span>
              </Button>
            )}
            {sheetError && <span className="max-w-60 truncate text-xs text-destructive" title={sheetError}>{sheetError}</span>}
          </>
        )}
        {/* The language every section is showing and editing; its menu opens Manage languages */}
        <LanguageSwitcher
          languages={langs.languages}
          value={langs.working}
          onChange={langs.setWorking}
          sourceLocale={langs.sections.find(s => s.section === sectionOf)?.sourceLocale ?? cms.store.defaultLocale}
          detailOf={code => {
            const c = langs.coverageFor(sectionOf, code)
            return c && c.total ? `${percent(c)}%` : undefined
          }}
          onManage={() => setLocalesOpen(true)}
        />

      </div>

      <LanguagesPanel
        open={localesOpen}
        onOpenChange={setLocalesOpen}
        languages={langs.languages}
        sections={langs.sections}
        sites={langs.sites}
        workingLocale={langs.working}
        onWorkingLocaleChange={langs.setWorking}
        onAddLanguage={langs.addLanguage}
        onAddToSection={langs.addToSection}
        removalBlockers={langs.removalBlockers}
        onRemoveLanguage={langs.removeLanguage}
        onCopy={handleCopyLocale}
      />

      {/* Main body */}
      <div className="flex flex-1 min-h-0">

        {/* Closed sidebar: a thin rail to bring it back */}
        {!sidebarOpen && (
          <div className="flex shrink-0 flex-col items-center border-r bg-background py-2">
            <Button
              variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={() => setSidebarOpen(true)}
              title={`Open ${BACKENDS.find(b => b.value === backend)?.browserTitle.toLowerCase()}`}
            >
              <PanelLeftOpen className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Left: Article browser */}
        {sidebarOpen && (
          <>
            <div
              className="shrink-0 border-r flex flex-col min-h-0 bg-background"
              // Never more than 45%, so a narrow window still leaves room for the editor
              style={{ width: sidebarWidth, maxWidth: "45%" }}
            >
              <div className="px-3 py-2 border-b shrink-0 flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs font-semibold flex-1">
                  {BACKENDS.find(b => b.value === backend)?.browserTitle}
                </span>
                <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-foreground" onClick={() => setSidebarOpen(false)} title="Close panel">
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="flex-1 min-h-0 overflow-hidden">
                <PendingIdsContext.Provider value={pendingIds}>
                {isSiteConfig ? (
                  <SiteConfigBrowser
                    store={siteconfig.store}
                    locale={siteconfig.locale}
                    language={workingLanguage}
                    countries={siteconfigCountries}
                    selected={siteconfig.selected}
                    onSelect={(kind, id, view) => { siteconfig.setSelected({ kind, id, view }); setEditorOpen(true) }}
                    onCreate={(kind, fields) => { const id = siteconfig.create(kind, fields); setEditorOpen(true); return id }}
                    onUpdate={siteconfig.update}
                    onDelete={(kind, id) => siteconfig.remove(kind, id)}
                  />
                ) : isSvc ? (
                  <ServiceBrowser
                    store={svc.store}
                    locale={svc.locale}
                    language={workingLanguage}
                    selected={svc.selected}
                    onSelect={(kind, id) => { svc.setSelected({ kind, id }); setEditorOpen(true) }}
                    onCreate={(kind, fields) => { svc.create(kind, fields); setEditorOpen(true) }}
                    onDelete={(kind, id) => svc.remove(kind, id)}
                    canEdit={atLeast(cmsPerms.serviceMap, "edit")}
                    recordInfo={(kind, id) => recordInfoFor(SVC_BUCKET[kind], id)}
                    people={people}
                  />
                ) : isCMS ? (
                  <ArticleTreeBrowser
                    store={cms.store}
                    locale={cms.locale}
                    language={workingLanguage}
                    selectedId={cms.selectedId}
                    onSelect={(id) => { cms.setSelectedId(id); setEditorOpen(true) }}
                    onCreateCategory={() => cms.createCategory()}
                    onCreateSection={(catId, parentId) => cms.createSection(catId, parentId)}
                    onCreateArticle={(secId) => { cms.createArticle(secId); setEditorOpen(true) }}
                    onDelete={(id) => cms.deleteNode(id)}
                    recordInfo={id => recordInfoFor("articles", id)}
                    people={people}
                  />
                ) : null}
                </PendingIdsContext.Provider>
              </div>
              <PendingChangesButton
                items={pendingItems}
                canPublish={sectionPending.canPublish}
                onPublish={key => { sectionPending.publish([key]); sectionMeta.clearReview(key) }}
                onPublishAll={() => { sectionPending.publish(); sectionPending.keys.forEach(sectionMeta.clearReview) }}
                onDiscard={key => { sectionPending.discard([key]); sectionMeta.clearReview(key) }}
                onOpen={openPending}
                due={dueItems}
                onVerify={key => sectionMeta.update(key, { verifiedAt: Date.now(), verifiedBy: myName })}
              />
              <SearchIndexStatus index={searchIndex} />
            </div>

            {/* Drag handle to resize the sidebar */}
            <div
              onMouseDown={() => startResize("sidebar")}
              onDoubleClick={() => setSidebarWidth(SIDEBAR_DEFAULT)}
              className="w-1 shrink-0 cursor-col-resize bg-border transition-colors hover:bg-primary/40"
              title="Drag to resize · double-click to reset"
              role="separator"
              aria-orientation="vertical"
            />
          </>
        )}

        {/*
          Nothing selected. The chat used to fill this space; now that it
          floats, an empty flex child would leave the sidebar stranded against
          a blank slab, so say what to do instead.
        */}
        {(!editorOpen || !(isCMS ? cms.selectedId : isSvc ? svc.selected : siteconfig.selected)) && (
          <div className="flex min-w-0 flex-1 flex-col items-center justify-center gap-3 border-l bg-muted/20 p-8 text-center">
            <BookOpen className="h-8 w-8 text-muted-foreground/40" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Nothing open</p>
              <p className="max-w-xs text-xs text-muted-foreground/70">
                Pick something from the {BACKENDS.find(b => b.value === backend)?.browserTitle.toLowerCase()} to edit it,
                or ask the assistant to build something for you.
              </p>
            </div>
            {assistant.enabled && !assistant.open && (
              <Button
                variant="outline" size="sm" className="h-7 gap-1.5 text-xs"
                onClick={() => assistant.setOpen(true)}
              >
                <Sparkles className="h-3.5 w-3.5" />
                Ask the assistant
              </Button>
            )}
          </div>
        )}

        {/* Right: node editor (local CMS) */}
        {isCMS && editorOpen && cms.selectedId && (
          <>
            <div
              className="flex min-w-0 flex-1 flex-col min-h-0 border-l bg-background"
            >
              <div className="flex shrink-0 items-center gap-1 border-b px-3 py-2">
                <span className="flex-1 text-xs font-semibold">Editor</span>
                <Button
                  variant="ghost" size="icon" className="h-8 w-8"
                  title="Close editor"
                  onClick={() => setEditorOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <EditorAccessNotice access={cmsPerms.articles} live={articleIsLive} staged={cms.pending.available} />
              <div className="min-h-0 flex-1 overflow-hidden">
                <ArticleNodeEditor
                  store={cms.store}
                  nodeId={cms.selectedId}
                  locale={cms.locale}
                  languages={langs.languages}
                  // Route through commitEdit so manual edits are logged too,
                  // not just the agent's.
                  onCommit={(next, summary) => cms.commitEdit(next, cms.selectedId ?? undefined, summary)}
                  onAddLocale={code => langs.addToSection("articles", code)}
                  versionsOf={cms.versionsOf}
                  restoreNodeVersion={cms.restoreNodeVersion}
                  status={articleStatus}
                  indexBadge={cms.selectedId && articleBucketOf(cms.selectedId) === "articles" && (
                    <>
                      <IndexBadge status={searchIndex.recordStatus("article", cms.selectedId)} loadDetail={() => searchIndex.recordDetail("article", cms.selectedId)} onReindex={() => void searchIndex.syncNow()} />
                      {freshnessChip("articles", cms.selectedId)}
                    </>
                  )}
                />
              </div>
            </div>
          </>
        )}

        {/* Right: entity editor (service map) */}
        {isSvc && editorOpen && svc.selected && (
          <>
            <div
              className="flex min-w-0 flex-1 flex-col min-h-0 border-l bg-background"
            >
              <EditorAccessNotice access={cmsPerms.serviceMap} live={serviceIsLive} staged={svc.pending.available} />
              <div className="min-h-0 flex-1 overflow-hidden">
                <ServiceEditor
                  store={svc.store}
                  locale={svc.locale}
                  languages={langs.languages}
                  kind={svc.selected.kind}
                  id={svc.selected.id}
                  onUpdate={svc.update}
                  onSetTranslation={svc.setTranslation}
                  onCommit={svc.commitEntity}
                  status={serviceStatus}
                  onAddLocale={code => langs.addToSection("serviceMap", code)}
                  onSelect={(kind, id) => svc.setSelected({ kind, id })}
                  onClose={() => setEditorOpen(false)}
                  indexBadge={(svc.selected.kind === "service" || svc.selected.kind === "provider") && (
                    <>
                      <IndexBadge status={searchIndex.recordStatus(svc.selected.kind, svc.selected.id)} loadDetail={() => searchIndex.recordDetail(svc.selected.kind as "service" | "provider", svc.selected.id)} onReindex={() => void searchIndex.syncNow()} />
                      {freshnessChip(SVC_BUCKET[svc.selected.kind], svc.selected.id)}
                    </>
                  )}
                  versionsOf={svc.versionsOf}
                  restoreEntityVersion={svc.restoreEntityVersion}
                />
              </div>
            </div>
          </>
        )}

        {/* Right: entity editor (site config) */}
        {isSiteConfig && editorOpen && siteconfig.selected && (
          <>
            <div
              className="flex min-w-0 flex-1 flex-col min-h-0 border-l bg-background"
            >
              <EditorAccessNotice access={cmsPerms.siteConfig} live={false} staged={siteconfig.pending.available} />
              <div className="min-h-0 flex-1 overflow-hidden">
                <SiteConfigEditor
                  store={siteconfig.store}
                  locale={siteconfig.locale}
                  languages={langs.languages}
                  countries={siteconfigCountries}
                  regions={siteconfigRegions}
                  cities={siteconfigCities}
                  articleCategories={siteconfigArticleCategories}
                  kind={siteconfig.selected.kind}
                  id={siteconfig.selected.id}
                  view={siteconfig.selected.view}
                  onUpdate={siteconfig.update}
                  indexBadge={siteconfig.selected.kind === "pageContent" && (
                    <IndexBadge status={searchIndex.recordStatus("block", siteconfig.selected.id)} loadDetail={() => searchIndex.recordDetail("block", siteconfig.selected.id)} onReindex={() => void searchIndex.syncNow()} />
                  )}
                  writeError={siteconfig.writeError}
                  onDismissWriteError={siteconfig.dismissWriteError}
                  onSetTranslation={siteconfig.setTranslation}
                  onCommit={siteconfig.commitEntity}
                  status={recordStatusFor(SITECONFIG_BUCKET[siteconfig.selected.kind], siteconfig.selected.id)}
                  onClose={() => setEditorOpen(false)}
                  versionsOf={siteconfig.versionsOf}
                  restoreEntityVersion={siteconfig.restoreEntityVersion}
                />
              </div>
            </div>
          </>
        )}

      </div>
    </div>
    </MediaSourcesContext.Provider>
  )
}
